import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  DollarSign,
  Clock,
  Users,
  Layers,
  CreditCard,
  Target,
  CheckCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import pricingModelMasterService from '../../services/pricingModelMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'

const PricingModelMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalPricingModels: 0,
    activePricingModels: 0,
    inactivePricingModels: 0,
    defaultPricingModels: 0,
    avgBaseRate: 0,
    totalHourlyRates: 0,
    totalDailyRates: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedPricingModelData, setSelectedPricingModelData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [pricingModelsResponse, statsResponse] = await Promise.all([
        pricingModelMasterService.getAllPricingModelMasters(),
        pricingModelMasterService.getPricingModelMasterStats()
      ])

      if (pricingModelsResponse.success) {
        setData(pricingModelsResponse.data)
        if (pricingModelsResponse.data.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No pricing models found. Create your first pricing model to get started.')
        }
      } else {
        toast.error('Failed to load pricing models')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading pricing model data:', error)
      toast.error('Error loading pricing model data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/pricing-model-master/new')
  }

  const handleEdit = (row) => {
    const cleanRowData = {
      _id: row._id,
      id: row.id,
      company: row.company,
      pricingModelCode: row.pricingModelCode,
      pricingModelName: row.pricingModelName,
      pricingModelDescription: row.pricingModelDescription,
      pricingType: row.pricingType,
      pricingModelStatus: row.pricingModelStatus,
      isDefault: row.isDefault,
      pricingConfiguration: row.pricingConfiguration,
      rateStructure: row.rateStructure,
      billingSettings: row.billingSettings,
      applicabilitySettings: row.applicabilitySettings,
      performanceMetrics: row.performanceMetrics,
      visualSettings: row.visualSettings,
      revisionHistory: row.revisionHistory,
      currentRevision: row.currentRevision,
      createdBy: row.createdBy,
      updatedBy: row.updatedBy,
      isArchived: row.isArchived,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt
    }
    
    navigate('/projects/pricing-model-master/edit', { 
      state: { pricingModelData: cleanRowData } 
    })
  }

  const handleView = (row) => {
    toast.info(`Viewing Pricing Model: ${row.pricingModelName}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await pricingModelMasterService.deletePricingModelMaster(deleteItem._id)
      if (response.success) {
        toast.success('Pricing model deleted successfully')
        loadData()
      } else {
        toast.error('Failed to delete pricing model')
      }
    } catch (error) {
      console.error('Error deleting pricing model:', error)
      toast.error('Error deleting pricing model')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (row) => {
    setSelectedPricingModelData(row)
    setShowRevisionHistory(true)
  }

  const getPricingTypeIcon = (pricingType) => {
    const iconMap = {
      'Time & Material': Clock,
      'Fixed Price': DollarSign,
      'Staff Augmentation': Users,
      'Hybrid': Layers,
      'Retainer': CreditCard,
      'Milestone-based': Target
    }
    return iconMap[pricingType] || DollarSign
  }

  const formatCurrency = (amount, currency = 'INR') => {
    if (!amount) return '0'
    
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatBillingUnit = (unit) => {
    const unitMap = {
      'Hour': '/hr',
      'Day': '/day',
      'Week': '/week',
      'Month': '/month',
      'Project': '/project',
      'Milestone': '/milestone'
    }
    return unitMap[unit] || unit
  }

  const transformedData = data.map(item => ({
    ...item,
    id: item._id || item.id,
    formattedBaseRate: item.pricingConfiguration?.baseRate
      ? `${formatCurrency(item.pricingConfiguration.baseRate, item.pricingConfiguration.currency)}${formatBillingUnit(item.pricingConfiguration.billingUnit)}`
      : 'N/A',
    formattedBillingFrequency: item.billingSettings?.billingFrequency || 'N/A',
    formattedPaymentTerms: item.billingSettings?.paymentTerms || 'N/A',
    currentVersion: `v${item.currentRevision || 1}`,
  }))

  const columns = [
    {
      accessorKey: 'pricingModelCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <span className="code-text">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'pricingModelName',
      header: 'Pricing Model',
      cell: ({ getValue, row }) => {
        const PricingTypeIcon = getPricingTypeIcon(row.original.pricingType)
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PricingTypeIcon size={15} style={{ color: 'var(--color-brand-cyan-dark, #51418F)', flexShrink: 0 }} />
            <span style={{ fontWeight: 500 }}>{getValue()}</span>
            {row.original.isDefault && (
              <span className="status-badge active" style={{ fontSize: '10px', padding: '1px 6px' }}>
                DEFAULT
              </span>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'pricingType',
      header: 'Type',
      cell: ({ getValue }) => (
        <span className="status-badge inactive">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'formattedBaseRate',
      header: 'Base Rate',
      cell: ({ getValue }) => (
        <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{getValue()}</span>
      ),
    },
    {
      accessorKey: 'formattedBillingFrequency',
      header: 'Billing',
    },
    {
      accessorKey: 'formattedPaymentTerms',
      header: 'Payment Terms',
    },
    {
      accessorKey: 'pricingModelStatus',
      header: 'Status',
      cell: ({ getValue }) => (
        <span className={`status-badge ${getValue() === 'Active' ? 'active' : 'inactive'}`}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'currentVersion',
      header: 'Version',
    },
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects' },
    { label: 'Masters', path: '/projects?tab=masters' },
    { label: 'Pricing Model Master', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Pricing Model Master</h1>
          <p>Manage pricing models for different project types and billing structures</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAddNew}>
            <Plus size={16} />
            Add New Pricing Model
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalPricingModels || 0}</div>
          <div className="metric-title">Total Pricing Models</div>
          <div className="metric-subtitle">All configured models</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activePricingModels || 0}</div>
          <div className="metric-title">Active Models</div>
          <div className="metric-subtitle">Ready for use</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Hourly</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalHourlyRates || 0}</div>
          <div className="metric-title">Hourly Rates</div>
          <div className="metric-subtitle">Time-based pricing</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Daily</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalDailyRates || 0}</div>
          <div className="metric-title">Daily Rates</div>
          <div className="metric-subtitle">Day-based pricing</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={transformedData}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          enableSelection={false}
          noCard={true}
          className="pricing-model-master-table"
          pageSize={10}
        />
      </div>

      {showRevisionHistory && selectedPricingModelData && (
        <RevisionHistoryModal
          isOpen={showRevisionHistory}
          onClose={() => setShowRevisionHistory(false)}
          data={selectedPricingModelData}
          title="Pricing Model Revision History"
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Pricing Model"
        message={`Are you sure you want to delete "${deleteItem?.pricingModelName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PricingModelMaster
